import { computed, Injectable, signal } from '@angular/core';
import { cloneDeep } from 'lodash-es';

import { FlatTreeNode, TreeNode } from '@treetop/shared/model';

import { TreeDragDropInstruction, TreeDragDropInstructionType } from './tree-drag-drop/tree-drag-drop.service';
import { findNodeById, flattenTree } from './tree.util';

export type TreeComponentServiceInitOptions<T> = {
  data: T[];
  selected: T[];
  expanded: T[];
  expandAll: boolean;
};

@Injectable()
export class TreeComponentService<T extends TreeNode<unknown>> {
  public allowMultipleSelection = false;
  public disableChildrenOnSelection = false;

  public waitForData = signal<boolean>(false);
  private dataSource = signal<T[]>([]);
  private expandedNodes = signal<T[]>([]);
  private disabledNodeIds = signal<Set<string>>(new Set<string>());

  private flattenedDataSource = computed(() => {
    const flattened = flattenTree(this.dataSource(), 0, null, this.expandedNodes(), this.selected());
    const disabledIds = this.disabledNodeIds();

    return flattened.map((node) => ({
      ...node,
      disabled: disabledIds.has(node.node.id),
    }));
  });

  public vm = computed(() => {
    return {
      dataSource: this.flattenedDataSource(),
      expandedNodes: this.expandedNodes(),
      waitForData: this.waitForData(),
    };
  });

  public selected = signal<T[]>([]);

  public init({ data, selected, expanded, expandAll }: Partial<TreeComponentServiceInitOptions<T>>) {
    this.updateDataSource(data);
    this.selected.set(selected);

    if (expandAll) {
      this.expandAll();
    } else {
      expanded.forEach((node) => {
        this.toggleExpandNode(node, 'open');
      });
    }

    this.selected().forEach((node) => {
      this.toggleExpandNode(node, 'open');
    });
  }

  /**
   * Used to set the data source of the tree from the component input
   */
  public setDataSource(data: T[]) {
    this.waitForData.set(false);
    this.updateDataSource(data);
  }

  /**
   * Used to refresh the data source of the tree so that the CDK component can detect the changes
   */
  private updateDataSource(data: T[]): void {
    this.dataSource.set(cloneDeep(data));
  }

  public removeNode(node: T): void {
    let currentTree = this.dataSource();
    const parent = findNodeById(node.parentId, currentTree);

    if (parent === undefined) {
      currentTree = currentTree.filter((n) => n.id !== node.id);
    } else if (parent.children) {
      parent.children = parent.children.filter((n) => n.id !== node.id);
    }

    this.updateDataSource(currentTree);

    this.selected.set(this.selected().filter((n) => n.id !== node.id));
    this.expandedNodes.set(this.expandedNodes().filter((n) => n.id !== node.id));
  }

  public toggleExpandNode(node: T, forceState?: 'open' | 'close', enforced = false) {
    const flatNode = this.flattenedDataSource().find((n) => n.node.id === node.id);
    if (!flatNode) {
      return;
    }

    const expandedNodes = this.expandedNodes();

    const expandParents = (currentNode: FlatTreeNode<T>) => {
      if (!currentNode.node.parentId) return;

      const parentNode = this.flattenedDataSource().find((n) => n.node.id === currentNode.node.parentId);
      if (parentNode) {
        parentNode.isExpanded = true;
        this.expandedNodes.set([...this.expandedNodes(), parentNode.node]);
        expandParents(parentNode);
      }
    };

    if (flatNode.isExpanded && forceState !== 'open') {
      this.expandedNodes.set(expandedNodes.filter((n) => n.id !== flatNode.node.id));
      flatNode.isExpanded = false;
      return;
    }

    if (!flatNode.isExpanded && forceState !== 'close') {
      if (flatNode.expandable || enforced) {
        this.expandedNodes.set([...expandedNodes, flatNode.node]);
        flatNode.isExpanded = true;
      }

      expandParents(flatNode);
    }
  }

  public addNode(node?: FlatTreeNode<T>): T {
    const newNode = {
      id: crypto.randomUUID(),
      title: '',
      parentId: node?.node.id,
      item: {
        order: node?.node.children?.length ?? this.dataSource().length,
      },
      children: [],
    } as T;

    const currentTree = this.dataSource();
    if (node) {
      const parent = findNodeById(node.node.id, currentTree);

      if (parent.children === undefined) {
        parent.children = [];
      }
      newNode.order = parent.children.length;

      parent.children.push(newNode);

      this.toggleExpandNode(node.node, 'open', true);
    } else {
      currentTree.push(newNode);
    }

    this.updateDataSource(currentTree);

    return findNodeById(newNode.id, currentTree);
  }

  public onDragDrop(node: T, target: T, instruction: TreeDragDropInstruction): T {
    const currentTree = this.dataSource();
    const actNode = findNodeById(node.id, currentTree);
    if (!actNode) {
      console.error('Node to be moved not found in the current tree.');
      return node;
    }

    const actTarget = findNodeById(target.id, currentTree);
    if (!actTarget) {
      console.error('Target node not found in the current tree.');
      return node;
    }

    if (instruction.type === 'make-child' && actTarget.id === actNode.id) {
      return;
    }

    const originalParent = findNodeById(actNode.parentId, currentTree);
    const originalSiblings = originalParent?.children ?? currentTree;

    originalSiblings.splice(originalSiblings.indexOf(actNode), 1);
    originalSiblings.forEach((n, i) => {
      n.order = i;
    });

    switch (instruction.type) {
      case TreeDragDropInstructionType.ReorderAbove:
      case TreeDragDropInstructionType.ReorderBelow: {
        const newSiblings = findNodeById(target.parentId, currentTree)?.children ?? currentTree;

        let targetIndex = newSiblings.indexOf(actTarget);
        if (instruction.type === TreeDragDropInstructionType.ReorderBelow) {
          targetIndex += 1;
        }

        actNode.parentId = target.parentId;

        newSiblings.splice(targetIndex, 0, actNode);
        newSiblings.forEach((n, i) => {
          n.order = i;
        });

        actNode.order = targetIndex;

        break;
      }

      case TreeDragDropInstructionType.MakeChild: {
        actNode.parentId = target.id;

        const newSiblings = findNodeById(actTarget.id, currentTree)?.children ?? currentTree;

        actNode.order = newSiblings.length;
        newSiblings.push(actNode);

        break;
      }
    }

    this.updateDataSource(currentTree);

    return actNode;
  }

  public updateNode(node: T) {
    const currentTree = this.dataSource();

    const actNode = findNodeById(node.id, currentTree);

    actNode.title = node.title;

    this.updateDataSource(currentTree);
  }

  public collapseAll() {
    this.expandedNodes.set([]);
  }

  public expandAll() {
    this.expandedNodes.set(this.flattenedDataSource().map((node) => node.node));
  }

  public selectNodes(nodes: T[] = []): void {
    if (!nodes || nodes.length === 0) {
      this.selected.set([]);
      return;
    }
    this.selected.set(nodes);
  }

  public selectNode(node: T): void {
    let selected = cloneDeep(this.selected());

    if (!node) {
      selected = [];
      this.selected.set(selected);
      return;
    }

    if (!this.allowMultipleSelection) {
      selected = [];
    }

    if (this.disableChildrenOnSelection) {
      const descendants = flattenTree([node], 0, null, [], [])
        .map((n) => n.node)
        .filter((n) => n.id !== node.id);
      this.disableNodes(descendants);
      const descendantIds = descendants.map((n) => n.id);
      selected = selected.filter((n) => !descendantIds.includes(n.id));
    }

    if (selected.find((n) => n.id === node.id)) {
      selected = selected.filter((n) => n.id !== node.id);
    } else {
      selected.push(node);
    }

    this.selected.set([...selected]);

    if (this.disableChildrenOnSelection && !selected?.length) {
      this.disableNodes([]);
    }
  }

  public disableNodes(nodes: TreeNode<unknown>[] = []): void {
    if (!nodes || nodes.length === 0) {
      this.disabledNodeIds.set(new Set<string>());
      return;
    }

    const disabledIds = new Set<string>(nodes.map((node) => node.id));
    this.disabledNodeIds.set(disabledIds);

    const currentSelected = this.selected();
    const filteredSelected = currentSelected.filter((node) => !disabledIds.has(node.id));

    if (filteredSelected.length !== currentSelected.length) {
      this.selected.set(filteredSelected);
    }
  }
}
